import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EmailGuard } from "../App";
import { AuthGuard } from "../App";
import { FaEye, FaSignOutAlt, FaTools, FaMoneyBillWave, FaSpinner, FaChartLine } from 'react-icons/fa';
import { Oval } from "react-loader-spinner"; // Assuming this is available
import { StandardURL } from "../App";

// Helper function to format currency
const formatCurrency = (amount) => {
  // Ensure the amount is treated as a number, defaulting to 0
  const numericAmount = parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-KE', { 
    style: 'currency', 
    currency: 'Ksh', 
    minimumFractionDigits: 0 
  }).format(numericAmount).replace('Ksh', 'Ksh '); // Add space after Ksh
};

// Component for a single metric card
const MetricCard = ({ title, value, icon: Icon, colorClass, currency = true }) => {
    return (
        <div className={`bg-white p-6 rounded-xl shadow-lg border-b-4 ${colorClass}`}>
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</h2>
                <Icon className={`w-6 h-6 ${colorClass.replace('border-', 'text-').replace('-600', '-500')}`} />
            </div>
            <p className="mt-4 text-3xl font-extrabold text-gray-900">
                {currency ? formatCurrency(value) : value}
            </p>
        </div>
    );
};

const MoneyInPage = () => {
  const [totalAgencyViewingFee, setTotalAgencyViewingFee] = useState(0);
  const [totalLeavingFee, setTotalLeavingFee] = useState(0);
  const [totalServiceFee, setTotalServiceFee] = useState(0);
  const [totalAllFees, setTotalAllFees] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setIsLoading(true);

    axios
      .all([
        axios.get(
          `${StandardURL}/iNyumba/money/TotalAgencyViewingFee`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${StandardURL}/iNyumba/money/TotalLeavingFee`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${StandardURL}/iNyumba/money/TotalServiceFee`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(`${StandardURL}/iNyumba/money/TotalAllFees`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ])
      .then(
        axios.spread(
          (
            totalAgencyViewingFeeRes,
            totalLeavingFeeRes,
            totalServiceFeeRes,
            totalAllFeesRes
          ) => {
            // Use fallback to 0 in case the API returns null or non-numeric response
            setTotalAgencyViewingFee(totalAgencyViewingFeeRes.data.response || 0);
            setTotalLeavingFee(totalLeavingFeeRes.data.response || 0);
            setTotalServiceFee(totalServiceFeeRes.data.response || 0);
            setTotalAllFees(totalAllFeesRes.data.response || 0);
            setIsLoading(false);
          }
        )
      )
      .catch((error) => {
        console.error("Error fetching data:", error);
        toast.error(error.response?.data?.response || "Error fetching financial data.");
        setIsLoading(false);
      });
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center mb-8">
                <FaChartLine className="mr-3 text-indigo-600" /> Financial Overview (Money In)
            </h1>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl shadow-xl">
                    <Oval color="#4f46e5" secondaryColor="#a5b4fc" height={50} width={50} ariaLabel="loading-spinner" />
                    <p className="mt-4 text-gray-600">Fetching latest financial metrics...</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    
                    {/* Total All Fees - Highlighted */}
                    <div className="lg:col-span-4">
                        <MetricCard
                            title="Total Revenue Generated"
                            value={totalAllFees}
                            icon={FaMoneyBillWave}
                            colorClass="border-green-600 bg-green-50 shadow-2xl"
                        />
                    </div>
                    
                    {/* Individual Fee Cards */}
                    <MetricCard
                        title="Agency Viewing Fee"
                        value={totalAgencyViewingFee}
                        icon={FaEye}
                        colorClass="border-indigo-600"
                    />
                    
                    <MetricCard
                        title="Leaving Fee (Tenant Exits)"
                        value={totalLeavingFee}
                        icon={FaSignOutAlt}
                        colorClass="border-red-600"
                    />
                    
                    <MetricCard
                        title="Service Fee"
                        value={totalServiceFee}
                        icon={FaTools}
                        colorClass="border-yellow-600"
                    />

                    {/* Placeholder Card (or can be removed if only 3 individual metrics) */}
                    <div className="bg-white p-6 rounded-xl shadow-lg border-b-4 border-gray-300 flex flex-col justify-center">
                        <p className="text-center text-gray-400 font-medium">Other metrics coming soon...</p>
                    </div>

                </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default MoneyInPage;